import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { useState, useCallback, useMemo } from "react";
import { View, Text, ScrollView, Pressable, Platform } from "react-native";
import {
  MessageSquare,
  Zap,
  Egg,
  FlaskConical,
  ArrowRightLeft,
  Stethoscope,
  Clock,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  type ContentBlock,
  type ProcedureBody,
  type ProcedureCategory,
} from "@src/packages/content";
import { useTranslations, useLocale } from "@src/packages/i18n";
import { EMBER } from "../../../onboarding/theme";
import type { ChatStackParamList } from "../../types";
import {
  MoonFilterChip,
  MoonSearchInput,
  BackLink,
  cardSurface,
} from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { useSmartBack, getBackLabel } from "../../hooks/useSmartBack";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { colors } from "@louise/theme/tokens";

/* ─── Category config ────────────────────────────────────────── */

const CATEGORY_CONFIG: {
  key: ProcedureCategory;
  i18nKey: string;
  icon: LucideIcon;
}[] = [
  { key: "consultation", i18nKey: "consultation", icon: MessageSquare },
  { key: "stimulation", i18nKey: "stimulation", icon: Zap },
  { key: "retrieval", i18nKey: "retrieval", icon: Egg },
  { key: "lab", i18nKey: "lab", icon: FlaskConical },
  { key: "transfer", i18nKey: "transfer", icon: ArrowRightLeft },
  { key: "diagnostic", i18nKey: "diagnostic", icon: Stethoscope },
  { key: "recovery", i18nKey: "recovery", icon: Clock },
];

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  consultation: MessageSquare,
  stimulation: Zap,
  retrieval: Egg,
  lab: FlaskConical,
  transfer: ArrowRightLeft,
  diagnostic: Stethoscope,
  recovery: Clock,
};

type Props = NativeStackScreenProps<ChatStackParamList, "Procedures">;

export default function ProceduresScreen({ route, navigation }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ProceduresScreen");
  const returnTab = route.params?.returnTab;
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Learn",
  });
  const backLabel =
    returnTab && returnTab !== "Learn"
      ? getBackLabel(returnTab, "Learn", t("you.shared.back"))
      : t("learn.back_to_learn");
  const { locale } = useLocale();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] =
    useState<ProcedureCategory | null>(null);
  const procedures = useMemo(() => getBlocksByKind("procedure"), []);

  // Reads body.frBody when active locale is FR — falls back to EN otherwise.
  // The ProcedureDetailSheet detail view already does this; the encyclopedia
  // card list needs the same treatment so cards aren't mixed-language.
  const fieldsFor = useCallback(
    (body: ProcedureBody) => {
      const fr = locale === "fr" ? body.frBody : undefined;
      return {
        procedureName: fr?.procedureName ?? body.procedureName,
        description: fr?.description ?? body.description,
        duration: fr?.duration ?? body.duration,
      };
    },
    [locale],
  );

  const filtered = useMemo(() => {
    let result = procedures;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((p) => {
        const f = fieldsFor(p.body);
        return (
          p.title.toLowerCase().includes(q) ||
          f.procedureName.toLowerCase().includes(q) ||
          f.description.toLowerCase().includes(q)
        );
      });
    }

    if (activeCategory) {
      result = result.filter((p) => p.body.category === activeCategory);
    }

    result.sort((a, b) => a.title.localeCompare(b.title));
    return result;
  }, [procedures, search, activeCategory, fieldsFor]);

  const onItemPress = useCallback(
    (item: ContentBlock<"procedure">) => {
      // Procedure names are authored catalogue copy, not patient data — the
      // visible (locale-aware) name is the label, the action stays stable.
      capturePress(
        pressLabel(fieldsFor(item.body).procedureName, item.title),
        "learn_procedure_card",
      );
      navigation.navigate("ProcedureDetail", { procedureId: item.id });
    },
    [capturePress, fieldsFor, navigation],
  );

  // Group by category for sectioned view
  const sections = useMemo(() => {
    if (activeCategory || search.trim()) {
      // When filtering, show flat list
      return [{ category: null, items: filtered }];
    }
    // Group by category
    const groups: {
      category: ProcedureCategory | null;
      items: ContentBlock<"procedure">[];
    }[] = [];
    for (const cat of CATEGORY_CONFIG) {
      const items = filtered.filter((p) => p.body.category === cat.key);
      if (items.length > 0) {
        groups.push({ category: cat.key, items });
      }
    }
    return groups;
  }, [filtered, activeCategory, search]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <BackLink
          label={backLabel}
          action="learn_encyclopedia_back"
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 38,
            fontWeight: "700",
            color: colors.deepBrown,
            lineHeight: 42,
            letterSpacing: -0.5,
            ...(Platform.OS === "web" && {
              ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }),
          }}
        >
          {t("learn.encyclopedia_screens.procedures.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
        >
          {t("learn.encyclopedia_screens.procedures.subtitle")}
        </Text>

        {/* Search bar */}
        <MoonSearchInput
          value={search}
          onChangeText={setSearch}
          placeholder={t(
            "learn.encyclopedia_screens.procedures.search_placeholder",
          )}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        {/* Category filter chips */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingVertical: 14,
          }}
        >
          <MoonFilterChip
            label={t("learn.encyclopedia_screens.all")}
            selected={!activeCategory}
            onPress={() => setActiveCategory(null)}
            tone="glass"
            showCheck={false}
          />
          {CATEGORY_CONFIG.map(({ key, i18nKey, icon: Icon }) => {
            const isActive = activeCategory === key;
            return (
              <MoonFilterChip
                key={key}
                label={t(
                  `learn.encyclopedia_screens.procedures.categories.${i18nKey}`,
                )}
                icon={Icon}
                selected={isActive}
                onPress={() => setActiveCategory(isActive ? null : key)}
                tone="glass"
                showCheck={false}
              />
            );
          })}
        </ScrollView>

        {/* Result count */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textFaint,
            marginBottom: 14,
          }}
        >
          {t(
            filtered.length === 1
              ? "learn.encyclopedia_screens.procedures.count_one"
              : "learn.encyclopedia_screens.procedures.count_other",
            { params: { count: String(filtered.length) } },
          )}
        </Text>

        {/* Sectioned list */}
        {sections.map((section) => {
          const catConfig = section.category
            ? CATEGORY_CONFIG.find((c) => c.key === section.category)
            : null;

          return (
            <View key={section.category ?? "all"} style={{ marginBottom: 24 }}>
              {catConfig && (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 12,
                  }}
                >
                  <catConfig.icon
                    size={15}
                    color={EMBER.colors.textMid}
                    strokeWidth={2}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 15,
                      fontWeight: "700",
                      color: EMBER.colors.text,
                    }}
                  >
                    {t(
                      `learn.encyclopedia_screens.procedures.categories.${catConfig.i18nKey}`,
                    )}
                  </Text>
                </View>
              )}

              <View style={{ gap: 8 }}>
                {section.items.map((proc) => {
                  const { body } = proc;
                  const CatIcon =
                    CATEGORY_ICONS[body.category ?? ""] ?? Stethoscope;
                  return (
                    <Pressable
                      key={proc.id}
                      onPress={() => onItemPress(proc)}
                      style={{
                        ...cardSurface(),
                        flexDirection: "row" as const,
                        alignItems: "center" as const,
                        paddingHorizontal: 16,
                        paddingVertical: 16,
                        gap: 14,
                      }}
                    >
                      <CatIcon
                        size={20}
                        color={EMBER.colors.coral}
                        strokeWidth={1.5}
                      />

                      <View style={{ flex: 1, gap: 2 }}>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 15,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                            lineHeight: 20,
                          }}
                          numberOfLines={1}
                        >
                          {fieldsFor(body).procedureName}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                            lineHeight: 18,
                          }}
                          numberOfLines={2}
                        >
                          {fieldsFor(body).description}
                        </Text>
                        {fieldsFor(body).duration && (
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 12,
                              color: EMBER.colors.textFaint,
                              marginTop: 2,
                            }}
                          >
                            {fieldsFor(body).duration}
                          </Text>
                        )}
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          );
        })}

        {/* Empty state */}
        {filtered.length === 0 && (
          <SearchEmptyState
            title={t("learn.encyclopedia_screens.procedures.empty")}
            onReset={() => {
              setSearch("");
              setActiveCategory(null);
            }}
          />
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
